import {formatTenThousandUnitsAmount} from '@financial/common-utils'

import {getCurrencyCodeUnit, getThousandTruncAmount, numberFormat} from '$utils/common'

interface DefaultFormatAmountOption {
    /**
     * 포메팅된 금액의 앞에 노출할 문자열
     *
     * ex) 연1,500원
     */
    prefix?: string
    /**
     * 다른나라 화폐 단위 사용시 사용
     *
     * ex) 1,000 USD, 900 JPY
     */
    currencyCode?: string | null
    /**
     * 원을 제외한 다른 화폐 단위 사용시 금액간 사이의 공백 여부
     *
     * ex) 100 JPY, 100JPY
     */
    noUnitSpace?: boolean
    /**
     * 양수일 때 앞에 + 부호를 붙일지 여부
     *
     * ex) +1,000
     */
    plusSign?: boolean
    /**
     * 0원일 때 + 부호를 붙일지 여부. plusSign이 true인 경우에만 적용됩니다
     *
     * ex) +0원
     */
    allowZeroSign?: boolean
}

/**
 * 기본 금액 표기 함수
 *
 * @description 기본 금액 표기 함수. 아래와 같이 포맷팅합니다
 *
 * 25000 => 25,000원
 *
 * 4290 => 4,290원
 *
 * 260 => 260원
 *
 * -1000 => -1,000원
 *
 * 0 => 0원
 *
 * 60 => 60원
 *
 * 정수 외 => ''
 *
 */
const 기본formatAmount = (amount: number, option?: DefaultFormatAmountOption) => {
    if (Number.isNaN(amount)) {
        return ''
    }

    const sign = (() => {
        const plusSign = option?.plusSign
        const allowZeroSign = option?.allowZeroSign

        if (!plusSign) {
            return ''
        }

        return amount > 0 || (allowZeroSign && amount === 0) ? '+' : ''
    })()

    const formattedAmount = (() => {
        const removedTrailingZeros = parseFloat(amount.toFixed(2)) // 소수점 최대 2자리 까지 노출, parseFloat 을 하면 소수점 이하 불필요한 0 은 제거된다.
        return numberFormat(removedTrailingZeros)
    })()

    const unit = getCurrencyCodeUnit(option?.currencyCode, {noUnitSpace: option?.noUnitSpace})

    const prefix = option?.prefix || ''

    return `${prefix}${sign}${formattedAmount}${unit}`
}

/**
 * 천만 단위 금액 표기 함수
 *
 * @description 보험 보장 분석에서 천만단위 금액을 사용하며 10만 이하 금액은 버림합니다, 아래와 같이 포맷팅합니다
 *
 * 250000000 => 2억 5천
 *
 * 42900000 => 4.2천 (십만 이하 버림)
 *
 * 2600000 => 0.2천
 *
 * -10000000 => -1천
 *
 * 0 => '0'
 *
 * 60 => '0'
 *
 * 정수 외 => ''
 *
 */
const 천만단위formatAmount = (amount: number) => {
    if (Number.isNaN(amount)) {
        return ''
    }

    const dividend = Math.trunc(Math.abs(amount) / 1000000) * 1000000 // 백만 이하 단위 절삭 절댓값
    if (dividend === 0) {
        return '0'
    }

    const sign = amount < 0 ? '-' : ''
    const quitient100MUnit = Math.trunc(dividend / 100000000) // 억 단위
    const remainder10MUnit = (dividend % 100000000) / 10000000 // 천만 단위

    const formattedQuotient = quitient100MUnit !== 0 ? `${numberFormat(quitient100MUnit)}억` : ''
    const formattedRemainder = remainder10MUnit !== 0 ? `${remainder10MUnit}천` : ''
    const separator = formattedQuotient && formattedRemainder ? ' ' : ''

    return `${sign}${formattedQuotient + separator + formattedRemainder}`
}

interface 만단위FormatAmountOption {
    dropLessThanThousand?: boolean
}
/**
 * 만 단위 금액 표기 포멧 함수
 *
 * @description 보험 보장 분석에서 사용하며, 아래와 같이 포맷팅합니다
 *
 * 250000000 => 2억 5,000만
 *
 * 42900000 => 4,290만 (십만 이하 버림)
 *
 * 2600000 => 260만
 *
 * -10000000 => -1,000만
 *
 * 8000000 => 800만
 *
 * 300000 => 30만
 *
 * 3000 => '0'
 *
 * 0 => '0'
 *
 * 60 => '0'
 *
 * 정수 외 => ''
 *
 */
const 만단위formatAmount = (amount: number, options?: 만단위FormatAmountOption) => {
    return (
        formatTenThousandUnitsAmount(getThousandTruncAmount(amount), {
            dropLessThanThousand: Boolean(options?.dropLessThanThousand),
        }) || '0'
    )
}

/**
 * 만 단위 금액 간단 소수점 표기 포멧 함수
 *
 * @description 보험 보장 분석에서 사용하며, 아래와 같이 포맷팅합니다
 *
 * 250000000 => 2.5억
 *
 * 42900000 => 4,290만
 *
 * 2600000 => 260만
 *
 * -10000000 => -1,000만
 *
 * 8000000 => 800만
 *
 * 300000 => 30만
 *
 * 3000 => 0.3만
 *
 * 0 => '0'
 *
 * 60 => '0'
 *
 * 정수 외 => ''
 *
 */
const 간단FormatAmount = (amount: number) => {
    if (Number.isNaN(amount)) {
        return ''
    }

    if (Math.abs(amount) < 10_000) {
        return '0'
    }

    if (Math.abs(amount) < 10_000_000) {
        return `${Math.trunc(amount / 10000)}만`
    }

    const sign = amount < 0 ? '-' : ''
    const dividend = Math.trunc(Math.abs(amount) / 1000000) * 1000000 // 백만 이하 단위 절삭 절댓값

    const quitient100MUnit = Math.trunc(dividend / 100000000) // 억 단위
    const remainder10MUnit = (dividend % 100000000) / 10000000 // 천만 단위

    const formattedQuotient = quitient100MUnit !== 0 ? `${numberFormat(quitient100MUnit)}억` : ''
    const formattedRemainder = remainder10MUnit !== 0 ? `${remainder10MUnit}천만` : ''

    if (!formattedQuotient) {
        return `${sign}${formattedRemainder}`
    }

    const 천만 = formattedRemainder[0] !== '0' ? formattedRemainder[0] : ''
    return `${sign}${quitient100MUnit}${천만 ? `.${천만}` : ''}억`
}

interface FormatAmountUtilType {
    type: '천만' | '간단'
}
type FormatUtilOption =
    | (DefaultFormatAmountOption & {type?: '기본'})
    | (만단위FormatAmountOption & {type: '만'})
    | FormatAmountUtilType

/**
 * 사용할 포메팅 유틸 타입
 *
 * 기본 - 기본 포멧 유틸. ex) 1_000_000 -> 1,000,000원
 *
 * 천만 - 천만단위 포멧 유틸. ex) 250_000_000 -> 2억 5천
 *
 * 만 - 만단위 포멧 유틸. ex) 250_000_000 -> 2억 5000만
 *
 * 간단 - 만단위 소수점 포멧 유틸. ex) 250_000_000 -> 2.5억
 */
export const formatAmount = (amount: number, option?: FormatUtilOption) => {
    if (typeof amount !== 'number' || Number.isNaN(amount)) {
        return ''
    }

    const utilType = option?.type

    if (utilType === '천만') {
        return 천만단위formatAmount(amount)
    }

    if (utilType === '만') {
        return 만단위formatAmount(amount, option)
    }

    if (utilType === '간단') {
        return 간단FormatAmount(amount)
    }

    if (utilType === '기본' || !utilType) {
        return 기본formatAmount(amount, option)
    }

    return ''
}
